import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { INomineeFormInput } from 'Pages/Life/BobLifeSteppers/NomineeDetails'
import { FieldValues } from 'react-hook-form'

interface IEcsDetails {
  ecsPayoutData: FieldValues
  ecsPayinData: FieldValues
}

export interface IProposalStepStoreValues {
  activeStep: number
  nomineeFormData: INomineeFormInput[]
  fatcaDeclarationChecked: boolean
  fatcaFormData: FieldValues
  fatcaDeclarationCheckedForPa: boolean
  fatcaFormDataForPa: FieldValues
  fatcaIsLaPaSame: boolean
  ecsFormData: IEcsDetails
  ecs_isPayinPayoutSame: boolean
  ecs_isDeclaration: boolean
  eiaFormData: FieldValues
  healthDetailsFormData: FieldValues
  healthDeclarationChecked: boolean
  customerConsentStatus: 'not-fetched' | 'pending' | 'success' | 'failed'
  paymentMethod: 'customerAccount' | 'DirectPayment' | 'DirectPaymentBOB' | null
  paymentLink: string
  paymentResponse: any
  isPaymentDone: boolean
  documents: Record<
    string,
    Record<
      string,
      {
        doc_tag: string
        doc_type: string
        urls: string[]
        customer_type: string
        isCkycDocUploaded: boolean
      }
    >
  >
  acrFormData: FieldValues
  isDocumentSubmitted:boolean
  isAcrFormSubmitted: boolean
  spConsentStatus: 'not-fetched' | 'pending' | 'success' | 'failed' | 'rejected'
  policySubmittedRes: any
  isCkycFetchedLa: boolean
  isCkycFetchedPa: boolean
  isQuickBuy: boolean
  journeyPurchaseType: '' | 'End To End' | 'Partial'
  consent_id: string
  consent_status: 'intial' | 'pending' | 'success'
  proposalSubmissionToInsurerRes:any
  illiterateAndVernacularData:FieldValues
  isdeclarationVerified:boolean
  fatcaFormDataForJointLife: FieldValues
  fatcaDeclarationCheckedForJointLife: boolean
}

const initialState: IProposalStepStoreValues = {
  activeStep: 0,
  nomineeFormData: [],
  fatcaDeclarationChecked: false,
  fatcaFormData: {},
  fatcaDeclarationCheckedForPa: false,
  fatcaFormDataForPa: {},
  fatcaIsLaPaSame: true,
  ecsFormData: {
    ecsPayoutData: {},
    ecsPayinData: {},
  },
  ecs_isDeclaration: false,
  ecs_isPayinPayoutSame: true,
  eiaFormData: {},
  healthDetailsFormData: {},
  healthDeclarationChecked: false,
  customerConsentStatus: 'not-fetched',
  paymentMethod: null,
  paymentLink: '',
  paymentResponse: '',
  isPaymentDone: false,
  documents: {},
  acrFormData: {},
  isDocumentSubmitted: false,
  isAcrFormSubmitted: false,
  spConsentStatus: 'not-fetched',
  policySubmittedRes: null,
  isCkycFetchedLa: false,
  isCkycFetchedPa: false,
  isQuickBuy: false,
  journeyPurchaseType: '',
  consent_id: '',
  consent_status: 'intial',
  proposalSubmissionToInsurerRes:null,
  illiterateAndVernacularData: {},
  isdeclarationVerified:false,
  fatcaFormDataForJointLife: {},
  fatcaDeclarationCheckedForJointLife: false,
}

const proposalStep = createSlice({
  name: 'proposalStep',
  initialState: initialState,
  reducers: {
    updateProposalStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload
    },
    updateNomineeDetails: (state, action: PayloadAction<INomineeFormInput[]>) => {
      action.payload
      state.nomineeFormData = action.payload
    },
    setFatcaDeclarationCheckbox: (state, action: PayloadAction<boolean>) => {
      state.fatcaDeclarationChecked = action.payload
    },
    updateFatcaDetails: (state, action: PayloadAction<FieldValues>) => {
      state.fatcaFormData = action.payload
    },
    setFatcaDeclarationForPaCheckbox: (state, action: PayloadAction<boolean>) => {
      state.fatcaDeclarationCheckedForPa = action.payload
    },
    updateFatcaDetailsForPa: (state, action: PayloadAction<FieldValues>) => {
      state.fatcaFormDataForPa = action.payload
    },
    updateFatcaIsLaPaSame: (state, action: PayloadAction<boolean>) => {
      state.fatcaIsLaPaSame = action.payload
    },
    updateEcsFormData: (state, action: PayloadAction<IEcsDetails>) => {
      state.ecsFormData = action.payload
    },
    updateEcsIsDeclaration: (state, action: PayloadAction<boolean>) => {
      state.ecs_isDeclaration = action.payload
    },
    updateEcsIsPayinPayoutSame: (state, action: PayloadAction<boolean>) => {
      state.ecs_isPayinPayoutSame = action.payload
    },
    updateEiaFormData: (state, action: PayloadAction<FieldValues>) => {
      state.eiaFormData = action.payload
    },
    updateHealthDetailsFormData: (state, action: PayloadAction<FieldValues>) => {
      state.healthDetailsFormData = action.payload
    },
    setHealthDeclarationCheckbox: (state, action: PayloadAction<boolean>) => {
      state.healthDeclarationChecked = action.payload
    },
    updateCustomerConsentStatus: (
      state,
      action: PayloadAction<'not-fetched' | 'pending' | 'success' | 'failed'>,
    ) => {
      state.customerConsentStatus = action.payload
    },
    updatePaymentMethod: (
      state,
      action: PayloadAction<'customerAccount' | 'DirectPayment' | 'DirectPaymentBOB'>,
    ) => {
      state.paymentMethod = action.payload
    },
    updatePaymentlink: (state, action: PayloadAction<string>) => {
      state.paymentLink = action.payload
    },
    updatePaymentResponse: (state, action: PayloadAction<any>) => {
      state.paymentResponse = action.payload
    },
    updateIsPaymentDone: (state, action: PayloadAction<boolean>) => {
      state.isPaymentDone = action.payload
    },
    updateDocuments: (
      state,
      action: PayloadAction<{
        doc_tag: string
        doc_type: string
        urls: string[]
        customer_type: string
        isCkycDocUploaded: boolean
      }>,
    ) => {
      if (!state.documents) {
        state.documents = {}
      }
      if (!state.documents[action.payload.customer_type]) {
        state.documents[action.payload.customer_type] = {}
      }
      state.documents[action.payload.customer_type][action.payload.doc_tag] = action.payload
    },
    updateSpConsentStatus: (
      state,
      action: PayloadAction<'not-fetched' | 'pending' | 'success' | 'failed' | 'rejected'>,
    ) => {
      state.spConsentStatus = action.payload
    },
    updateAcrFormData: (state, action: PayloadAction<FieldValues>) => {
      state.acrFormData = action.payload
    },
    updateIsDocumentSubmitted: (state, action: PayloadAction<boolean>) => {
      state.isDocumentSubmitted = action.payload
    },
    updateIsAcrFormSubmitted: (state, action: PayloadAction<boolean>) => {
      state.isAcrFormSubmitted = action.payload
    },
    updatePolicySubmittedRes: (state, action: PayloadAction<any>) => {
      state.policySubmittedRes = action.payload
    },
    updateIsCkycFetchedLa: (state, action: PayloadAction<boolean>) => {
      state.isCkycFetchedLa = action.payload
    },
    updateIsCkycFetchedPa: (state, action: PayloadAction<boolean>) => {
      state.isCkycFetchedPa = action.payload
    },
    updateIsQuickBuy: (state, action: PayloadAction<boolean>) => {
      state.isQuickBuy = action.payload
    },
    updateJourneyPurchaseType: (state, action: PayloadAction<'' | 'End To End' | 'Partial'>) => {
      state.journeyPurchaseType = action.payload
    },
    updateConsentId: (state, action: PayloadAction<string>) => {
      state.consent_id = action.payload
    },
    updateConsentStatus: (state, action: PayloadAction<'intial' | 'pending' | 'success'>) => {
      state.consent_status = action.payload
    },
    resetProposalStep: (state) => {
      state.activeStep = 0
    },
    updateProposalSubmissionToInsurerRes: (state, action: PayloadAction<any>) => {
      state.proposalSubmissionToInsurerRes = action.payload
    },
    updateIlliterateAndVernacularData: (state, action: PayloadAction<FieldValues>) => {
      state.illiterateAndVernacularData = action.payload
    },
    updateIsdeclarationVerified: (state, action: PayloadAction<boolean>) => {
      state.isdeclarationVerified = action.payload
    },
    updateFatcaDetailsForJointLife: (state, action: PayloadAction<FieldValues>) => {
      state.fatcaFormDataForJointLife = action.payload
    },
    setFatcaDeclarationForJointLifeCheckbox: (state, action: PayloadAction<boolean>) => {
      state.fatcaDeclarationCheckedForJointLife = action.payload
    },
    updateProposalStepAllData: (state, action: PayloadAction<IProposalStepStoreValues>) => {
      const {
        activeStep,
        nomineeFormData,
        fatcaDeclarationChecked,
        fatcaFormData,
        fatcaDeclarationCheckedForPa,
        fatcaFormDataForPa,
        fatcaIsLaPaSame,
        ecsFormData,
        ecs_isPayinPayoutSame,
        eiaFormData,
        healthDetailsFormData,
        healthDeclarationChecked,
        customerConsentStatus,
        paymentLink,
        paymentMethod,
        paymentResponse,
        isPaymentDone,
        documents,
        ecs_isDeclaration,
        acrFormData,
        isAcrFormSubmitted,
        spConsentStatus,
        policySubmittedRes,
        isCkycFetchedLa,
        isCkycFetchedPa,
        isQuickBuy,
        journeyPurchaseType,
        consent_id,
        consent_status,
        isDocumentSubmitted,
        proposalSubmissionToInsurerRes,
        illiterateAndVernacularData,
        isdeclarationVerified,
        fatcaFormDataForJointLife,
        fatcaDeclarationCheckedForJointLife,
      } = action.payload
      if (activeStep !== undefined) {
        state.activeStep = activeStep
      }
      if (nomineeFormData !== undefined) {
        state.nomineeFormData = nomineeFormData
      }
      if (fatcaDeclarationChecked !== undefined) {
        state.fatcaDeclarationChecked = fatcaDeclarationChecked
      }
      if (fatcaFormData !== undefined) {
        state.fatcaFormData = fatcaFormData
      }
      if (fatcaDeclarationCheckedForPa !== undefined) {
        state.fatcaDeclarationCheckedForPa = fatcaDeclarationCheckedForPa
      }
      if (fatcaFormDataForPa !== undefined) {
        state.fatcaFormDataForPa = fatcaFormDataForPa
      }
      if (fatcaIsLaPaSame !== undefined) {
        state.fatcaIsLaPaSame = fatcaIsLaPaSame
      }
      if (ecsFormData !== undefined) {
        state.ecsFormData = ecsFormData
      }
      if (ecs_isPayinPayoutSame !== undefined) {
        state.ecs_isPayinPayoutSame = ecs_isPayinPayoutSame
      }
      if (eiaFormData !== undefined) {
        state.eiaFormData = eiaFormData
      }
      if (healthDetailsFormData !== undefined) {
        state.healthDetailsFormData = healthDetailsFormData
      }
      if (healthDeclarationChecked !== undefined) {
        state.healthDeclarationChecked = healthDeclarationChecked
      }
      if (customerConsentStatus !== undefined) {
        state.customerConsentStatus = customerConsentStatus
      }
      if (paymentLink !== undefined) {
        state.paymentLink = paymentLink
      }
      if (paymentMethod !== undefined) {
        state.paymentMethod = paymentMethod
      }
      if (paymentResponse !== undefined) {
        state.paymentResponse = paymentResponse
      }
      if (isPaymentDone !== undefined) {
        state.isPaymentDone = isPaymentDone
      }
      if (documents !== undefined) {
        state.documents = documents
      }
      if (ecs_isDeclaration !== undefined) {
        state.ecs_isDeclaration = ecs_isDeclaration
      }
      if (acrFormData !== undefined) {
        state.acrFormData = acrFormData
      }
      if (isAcrFormSubmitted !== undefined) {
        state.isAcrFormSubmitted = isAcrFormSubmitted
      }
      if (isDocumentSubmitted !== undefined) {
        state.isDocumentSubmitted = isDocumentSubmitted
      }
      if (spConsentStatus !== undefined) {
        state.spConsentStatus = spConsentStatus
      }
      if (policySubmittedRes !== undefined) {
        state.policySubmittedRes = policySubmittedRes
      }
      if (isCkycFetchedLa !== undefined) {
        state.isCkycFetchedLa = isCkycFetchedLa
      }
      if (isCkycFetchedPa !== undefined) {
        state.isCkycFetchedPa = isCkycFetchedPa
      }
      if (isQuickBuy !== undefined) {
        state.isQuickBuy = isQuickBuy
      }
      if (journeyPurchaseType !== undefined) {
        state.journeyPurchaseType = journeyPurchaseType
      }
      if (consent_id !== undefined) {
        state.consent_id = consent_id
      }
      if (consent_status !== undefined) {
        state.consent_status = consent_status
      }
      if (proposalSubmissionToInsurerRes !== undefined) {
        state.proposalSubmissionToInsurerRes = proposalSubmissionToInsurerRes
      }
      if (illiterateAndVernacularData !== undefined) {
        state.illiterateAndVernacularData = illiterateAndVernacularData
      }
      if (isdeclarationVerified !== undefined) {
        state.isdeclarationVerified = isdeclarationVerified
      }
      if (fatcaFormDataForJointLife !== undefined) {
        state.fatcaFormDataForJointLife = fatcaFormDataForJointLife
      }
      if (fatcaDeclarationCheckedForJointLife !== undefined) {
        state.fatcaDeclarationCheckedForJointLife = fatcaDeclarationCheckedForJointLife
      }
      
    },
    resetAllProposalStepData: () => initialState,
  },
})


const { reducer } = proposalStep
const {
  updateProposalStep,
  resetProposalStep,
  updateNomineeDetails,
  setFatcaDeclarationCheckbox,
  updateFatcaDetails,
  setFatcaDeclarationForPaCheckbox,
  updateFatcaDetailsForPa,
  updateFatcaIsLaPaSame,
  updateEcsFormData,
  updateEcsIsPayinPayoutSame,
  updateEiaFormData,
  updateHealthDetailsFormData,
  setHealthDeclarationCheckbox,
  updateProposalStepAllData,
  resetAllProposalStepData,
  updateCustomerConsentStatus,
  updatePaymentMethod,
  updatePaymentlink,
  updatePaymentResponse,
  updateIsPaymentDone,
  updateEcsIsDeclaration,
  updateDocuments,
  updateSpConsentStatus,
  updateAcrFormData,
  updateIsDocumentSubmitted,
  updateIsAcrFormSubmitted,
  updatePolicySubmittedRes,
  updateIsCkycFetchedLa,
  updateIsCkycFetchedPa,
  updateIsQuickBuy,
  updateJourneyPurchaseType,
  updateConsentId,
  updateConsentStatus,
  updateProposalSubmissionToInsurerRes,
  updateIlliterateAndVernacularData,
  updateIsdeclarationVerified,
  updateFatcaDetailsForJointLife,
  setFatcaDeclarationForJointLifeCheckbox
} = proposalStep.actions

export {
  reducer as proposalStepReducer,
  updateProposalStep,
  resetProposalStep,
  updateNomineeDetails,
  setFatcaDeclarationCheckbox,
  updateFatcaDetails,
  setFatcaDeclarationForPaCheckbox,
  updateFatcaDetailsForPa,
  updateFatcaIsLaPaSame,
  updateEcsFormData,
  updateEcsIsPayinPayoutSame,
  updateEiaFormData,
  updateHealthDetailsFormData,
  setHealthDeclarationCheckbox,
  updateProposalStepAllData,
  updateEcsIsDeclaration,
  resetAllProposalStepData,
  updateCustomerConsentStatus,
  updatePaymentMethod,
  updatePaymentlink,
  updatePaymentResponse,
  updateIsPaymentDone,
  updateDocuments,
  updateSpConsentStatus,
  updateAcrFormData,
  updateIsDocumentSubmitted,
  updateIsAcrFormSubmitted,
  updatePolicySubmittedRes,
  updateIsCkycFetchedLa,
  updateIsCkycFetchedPa,
  updateIsQuickBuy,
  updateJourneyPurchaseType,
  updateConsentId,
  updateConsentStatus,
  updateProposalSubmissionToInsurerRes,
  updateIlliterateAndVernacularData,
  updateIsdeclarationVerified,
  updateFatcaDetailsForJointLife,
  setFatcaDeclarationForJointLifeCheckbox
}
